import { differenceInHours, differenceInMinutes, format, isToday, isTomorrow } from "date-fns";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DISMISS_STORAGE_KEY, IMMINENT_THRESHOLD_MIN, URGENCY_LEVELS } from "./constants";

function MaintenanceWarning({ upcoming, onDismiss }) {
  const [isDismissed, setIsDismissed] = useState(false);
  const [, forceUpdate] = useState(0);

  const minutesUntilStart = useMemo(() => {
    if (!upcoming?.scheduledStart) return null;
    return Math.max(0, differenceInMinutes(new Date(upcoming.scheduledStart), new Date()));
  }, [upcoming?.scheduledStart]);

  const isImminent = minutesUntilStart !== null && minutesUntilStart < IMMINENT_THRESHOLD_MIN;

  useEffect(() => {
    const interval = setInterval(() => forceUpdate((n) => n + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const stored = sessionStorage.getItem(DISMISS_STORAGE_KEY);
    if (stored) {
      const { id, time } = JSON.parse(stored);
      const hoursSinceDismiss = differenceInHours(new Date(), new Date(time));
      if (id === upcoming?._id && hoursSinceDismiss < 1 && !isImminent) {
        setIsDismissed(true);
      } else if (isImminent) {
        sessionStorage.removeItem(DISMISS_STORAGE_KEY);
        setIsDismissed(false);
      }
    }
  }, [upcoming?._id, isImminent]);

  const getUrgencyLevel = useCallback(() => {
    if (minutesUntilStart === null) return URGENCY_LEVELS.MEDIUM;
    if (minutesUntilStart < 15) return URGENCY_LEVELS.CRITICAL;
    if (minutesUntilStart < 60) return URGENCY_LEVELS.HIGH;
    if (minutesUntilStart < 720) return URGENCY_LEVELS.MEDIUM;
    return URGENCY_LEVELS.LOW;
  }, [minutesUntilStart]);

  const getTimeText = useCallback(() => {
    if (minutesUntilStart === null || minutesUntilStart <= 0) return "starting now";

    const start = new Date(upcoming.scheduledStart);
    const timeStr = format(start, "h:mm a");

    if (minutesUntilStart < 60) return `in ${minutesUntilStart} minutes`;

    const hours = Math.floor(minutesUntilStart / 60);
    if (hours < 12) return `in ${hours} hour${hours > 1 ? "s" : ""}`;

    if (isToday(start)) return `today at ${timeStr}`;
    if (isTomorrow(start)) return `tomorrow at ${timeStr}`;
    return `on ${format(start, "EEE, MMM d")} at ${timeStr}`;
  }, [minutesUntilStart, upcoming?.scheduledStart]);

  const getDuration = useCallback(() => {
    if (!upcoming?.scheduledStart || !upcoming?.scheduledEnd) return null;
    const minutes = differenceInMinutes(new Date(upcoming.scheduledEnd), new Date(upcoming.scheduledStart));
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours} hour${hours > 1 ? "s" : ""}`;
  }, [upcoming?.scheduledStart, upcoming?.scheduledEnd]);

  const handleDismiss = () => {
    sessionStorage.setItem(
      DISMISS_STORAGE_KEY,
      JSON.stringify({ id: upcoming._id, time: Date.now() })
    );
    setIsDismissed(true);
    onDismiss?.();
  };

  if (isDismissed || !upcoming) return null;

  const urgency = getUrgencyLevel();
  const duration = getDuration();
  const canDismiss = !isImminent;

  return (
    <div className="tw-fixed tw-top-0 tw-left-0 tw-right-0 tw-z-50">
      <div
        style={{
          backgroundColor: "#FFFFFF",
          borderBottom: "1px solid #E5E7EB",
          boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
        }}
      >
        <div className="tw-container tw-mx-auto tw-px-4 tw-py-2.5 tw-flex tw-items-center tw-justify-between">
          <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
            <span
              className="tw-flex tw-items-center tw-gap-2 tw-px-2.5 tw-py-1 tw-rounded-full tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide"
              style={{ backgroundColor: urgency.bgColor, color: urgency.textColor }}
            >
              <span
                className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-animate-pulse"
                style={{ backgroundColor: urgency.dotColor }}
              />
              {urgency.label}
            </span>
            <span className="tw-text-sm" style={{ color: "#374151" }}>
              <span className="tw-font-medium tw-text-gray-900 tw-hidden sm:tw-inline">
                {upcoming?.title || "System update"} ·{" "}
              </span>
              Starting {getTimeText()}
              {duration && (
                <span className="tw-text-gray-500 tw-ml-2">
                  · Est. Duration: {duration}
                </span>
              )}
            </span>
          </div>

          {canDismiss && (
            <button
              onClick={handleDismiss}
              className="tw-p-1 tw-rounded tw-transition-colors hover:tw-bg-gray-100 tw-flex-shrink-0"
              style={{ color: "#6B7280" }}
              aria-label="Dismiss"
            >
              <svg className="tw-w-4 tw-h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

MaintenanceWarning.propTypes = {
  upcoming: PropTypes.shape({
    _id: PropTypes.string,
    title: PropTypes.string,
    scheduledStart: PropTypes.string,
    scheduledEnd: PropTypes.string,
  }),
  onDismiss: PropTypes.func,
};

export default MaintenanceWarning;
